import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { Loader2, Settings2 } from 'lucide-react';

interface ReferralConfig {
  enabled: boolean;
  referrer_discount_percent: number;
  referred_discount_percent: number;
  reward_valid_days: number;
  emails_enabled: boolean;
}

const DEFAULTS: ReferralConfig = {
  enabled: true,
  referrer_discount_percent: 15,
  referred_discount_percent: 10,
  reward_valid_days: 90,
  emails_enabled: true,
};

export default function ReferralProgramConfigCard() {
  const qc = useQueryClient();
  const [form, setForm] = useState<ReferralConfig>(DEFAULTS);

  const { data, isLoading } = useQuery({
    queryKey: ['referral-config'],
    queryFn: async () => {
      const { data, error } = await (supabase as any).rpc('referral_config');
      if (error) throw error;
      return (data ?? {}) as Partial<ReferralConfig>;
    },
  });

  useEffect(() => {
    if (!data) return;
    setForm({
      enabled: data.enabled ?? DEFAULTS.enabled,
      referrer_discount_percent: Number(data.referrer_discount_percent ?? DEFAULTS.referrer_discount_percent),
      referred_discount_percent: Number(data.referred_discount_percent ?? DEFAULTS.referred_discount_percent),
      reward_valid_days: Number(data.reward_valid_days ?? DEFAULTS.reward_valid_days),
      emails_enabled: data.emails_enabled ?? DEFAULTS.emails_enabled,
    });
  }, [data]);

  const save = useMutation({
    mutationFn: async () => {
      const { error } = await (supabase as any).rpc('admin_update_referral_config', { _cfg: form });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['referral-config'] });
      toast({ title: 'Configuración guardada' });
    },
    onError: (e: any) =>
      toast({
        title: 'No se pudo guardar',
        description: String(e?.message ?? '').includes('not_authorized')
          ? 'No tienes permisos para modificar el programa.'
          : 'Revisa los valores ingresados.',
        variant: 'destructive',
      }),
  });

  const num = (v: string) => Math.max(0, Number(v) || 0);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Settings2 className="w-4 h-4 text-primary" />
          Configuración del programa
        </CardTitle>
        <CardDescription>Porcentajes de recompensa, vigencia y notificaciones.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-medium">Programa activo</p>
                <p className="text-xs text-muted-foreground">Si se desactiva, no se registran nuevos referidos.</p>
              </div>
              <Switch checked={form.enabled} onCheckedChange={v => setForm(f => ({ ...f, enabled: v }))} />
            </div>

            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div>
                <p className="text-sm font-medium">Correos automáticos</p>
                <p className="text-xs text-muted-foreground">Avisos de registro, bienvenida y conversión.</p>
              </div>
              <Switch checked={form.emails_enabled} onCheckedChange={v => setForm(f => ({ ...f, emails_enabled: v }))} />
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="rp">Descuento al referidor (%)</Label>
                <Input id="rp" type="number" min={0} max={100} value={form.referrer_discount_percent}
                  onChange={e => setForm(f => ({ ...f, referrer_discount_percent: num(e.target.value) }))} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dp">Descuento al referido (%)</Label>
                <Input id="dp" type="number" min={0} max={100} value={form.referred_discount_percent}
                  onChange={e => setForm(f => ({ ...f, referred_discount_percent: num(e.target.value) }))} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="dv">Vigencia recompensa (días)</Label>
                <Input id="dv" type="number" min={1} max={3650} value={form.reward_valid_days}
                  onChange={e => setForm(f => ({ ...f, reward_valid_days: num(e.target.value) }))} />
              </div>
            </div>

            <div className="flex justify-end">
              <Button onClick={() => save.mutate()} disabled={save.isPending}>
                {save.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Guardar cambios
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
